import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Loader2, Star } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from "../../components/AccessRestricted";
import { api } from "../../utils/api";
import { toast } from "sonner";

interface Candidate {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  address: string | null;
  currentPosition: string | null;
  currentCompany: string | null;
  source: string | null;
  yearsExperience: number | null;
  linkedInProfile: string | null;
  status: string;
  overallRating: number | null;
  assessmentNotes: string | null;
  requisition: {
    id: number;
    jobRole: { id: number; roleName: string } | null;
  } | null;
}

const EditCandidate: React.FC = () => {
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();
  const { id } = useParams();


  const canUpdateCandidate = ability.can(ACTIONS.UPDATE, RESOURCES.CANDIDATE);
  if (!canUpdateCandidate) {
    return <AccessRestricted />;
  }

  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [linkedInProfile, setLinkedInProfile] = useState("");
  const [currentPosition, setCurrentPosition] = useState("");
  const [currentCompany, setCurrentCompany] = useState("");
  const [yearsExperience, setYearsExperience] = useState("");
  const [overallRating, setOverallRating] = useState("");
  const [assessmentNotes, setAssessmentNotes] = useState("");

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.get<{ success: boolean; candidate: Candidate }>(`/api/candidates/${id}`)
      .then((res) => {
        if (res.success) {
          const c = res.candidate;
          setCandidate(c);
          setFirstName(c.firstName || "");
          setLastName(c.lastName || "");
          setEmail(c.email || "");
          setPhone(c.phone || "");
          setAddress(c.address || "");
          setLinkedInProfile(c.linkedInProfile || "");
          setCurrentPosition(c.currentPosition || "");
          setCurrentCompany(c.currentCompany || "");
          setYearsExperience(c.yearsExperience?.toString() || "");
          setOverallRating(c.overallRating?.toString() || "");
          setAssessmentNotes(c.assessmentNotes || "");
        }
      })
      .catch((err: any) => {
        toast.error(err.message || "Failed to load candidate");
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleSubmit = async () => {
    if (!id) return;
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      toast.error("First name, last name and email are required");
      return;
    }
    setSaving(true);
    try {
      const res = await api.put<{ success: boolean; candidate: Candidate }>(
        `/api/candidates/${id}`,
        {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          phone: phone.trim() || null,
          address: address.trim() || null,
          linkedInProfile: linkedInProfile.trim() || null,
          currentPosition: currentPosition.trim() || null,
          currentCompany: currentCompany.trim() || null,
          yearsExperience: yearsExperience ? parseInt(yearsExperience) : null,
          overallRating: overallRating ? parseInt(overallRating) : null,
          assessmentNotes: assessmentNotes.trim() || null,
        }
      );
      if (res.success) {
        toast.success("Candidate updated successfully");
        navigate(`/recruitment/candidates/${id}`);
      }
    } catch (err: any) {
      // Error toast already shown by api.ts; don't duplicate
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 animate-in fade-in duration-500">
        <Loader2 size={32} className="animate-spin text-gray-400" />
        <p className="mt-3 text-sm font-medium text-gray-500">Loading candidate...</p>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="flex flex-col items-center justify-center py-20 animate-in fade-in duration-500">
        <p className="text-sm font-medium text-gray-500">Candidate not found.</p>
        <Button variant="outline" className="rounded-xl mt-4" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </div>
    );
  }

  const fullName = `${candidate.firstName} ${candidate.lastName}`;

  return (
    <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
      {/* Back Button */}
      <button
        onClick={() => navigate(`/recruitment/candidates/${id}`)}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors w-fit"
      >
        <ChevronLeft size={18} />
        <span className="text-sm font-medium">Back to {fullName}</span>
      </button>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Edit Candidate</h1>
        <p className="text-sm text-gray-500 font-medium mt-1">Update details for {fullName}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
            <h4 className="font-bold text-gray-900 text-base">Personal Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-bold text-gray-900">First Name</label>
                <Input
                  className="bg-gray-50 border-gray-100 rounded-lg h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-bold text-gray-900">Last Name</label>
                <Input
                  className="bg-gray-50 border-gray-100 rounded-lg h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-bold text-gray-900">Email</label>
                <Input
                  type="email"
                  className="bg-gray-50 border-gray-100 rounded-lg h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-bold text-gray-900">Phone</label>
                <Input
                  className="bg-gray-50 border-gray-100 rounded-lg h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <label className="text-sm font-bold text-gray-900">Address</label>
                <Input
                  className="bg-gray-50 border-gray-100 rounded-lg h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <label className="text-sm font-bold text-gray-900">LinkedIn Profile</label>
                <Input
                  className="bg-gray-50 border-gray-100 rounded-lg h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                  value={linkedInProfile}
                  onChange={(e) => setLinkedInProfile(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Current Position */}
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
            <h4 className="font-bold text-gray-900 text-base">Current Position</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-bold text-gray-900">Position</label>
                <Input
                  className="bg-gray-50 border-gray-100 rounded-lg h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                  value={currentPosition}
                  onChange={(e) => setCurrentPosition(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-bold text-gray-900">Company</label>
                <Input
                  className="bg-gray-50 border-gray-100 rounded-lg h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                  value={currentCompany}
                  onChange={(e) => setCurrentCompany(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-bold text-gray-900">Years Experience</label>
                <Input
                  type="number"
                  min={0}
                  className="bg-gray-50 border-gray-100 rounded-lg h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                  value={yearsExperience}
                  onChange={(e) => setYearsExperience(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Overall Assessment */}
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
            <h4 className="font-bold text-gray-900 text-base">Overall Assessment</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-bold text-gray-900">Overall Rating (1-10)</label>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setOverallRating(String(n))}
                      className="p-0.5 transition-colors"
                    >
                      <Star
                        size={22}
                        className={n <= parseInt(overallRating || "0") ? "text-amber-400 fill-amber-400" : "text-gray-200"}
                      />
                    </button>
                  ))}
                  {overallRating && (
                    <span className="ml-2 text-sm font-bold text-gray-900">{overallRating}/10</span>
                  )}
                </div>
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <label className="text-sm font-bold text-gray-900">Assessment Notes</label>
                <Textarea
                  placeholder="Assessment notes..."
                  className="bg-gray-50 border-gray-100 rounded-xl text-sm font-medium min-h-[120px] resize-y focus:ring-2 focus:ring-blue-100"
                  value={assessmentNotes}
                  onChange={(e) => setAssessmentNotes(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Summary + Actions */}
        <div className="lg:col-span-1 space-y-6">
          {/* Candidate Summary */}
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
            <h4 className="font-bold text-gray-900 text-base">Candidate Summary</h4>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Name</p>
                <p className="text-sm font-bold text-gray-900 mt-0.5">{fullName}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Email</p>
                <p className="text-sm font-bold text-gray-900 mt-0.5">{candidate.email}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Status</p>
                <p className="text-sm font-bold text-gray-900 mt-0.5">{candidate.status.replace(/_/g, " ")}</p>
              </div>
              {candidate.requisition?.jobRole?.roleName && (
                <div>
                  <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Applied For</p>
                  <p className="text-sm font-bold text-gray-900 mt-0.5">{candidate.requisition.jobRole.roleName}</p>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <Button
              className="w-full bg-slate-950 hover:bg-slate-900 text-white flex items-center justify-center gap-2 h-11 rounded-xl text-sm font-bold"
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? (
                <Loader2 size={16} className="animate-spin" />
              ) : null}
              <span>{saving ? "Saving..." : "Save Changes"}</span>
            </Button>
            <Button
              variant="outline"
              className="w-full bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-11 rounded-xl text-sm font-bold"
              onClick={() => navigate(`/recruitment/candidates/${id}`)}
              disabled={saving}
            >
              <span>Cancel</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditCandidate;
